import { Button } from '@ctw/shared/components/button';
import { LoadingSpinner } from '@ctw/shared/components/loading-spinner';
import type { TelemetryUserInteraction } from '@ctw/shared/context/telemetry/user-interactions';
import { tw } from '@ctw/shared/utils/tailwind';
import type { IconDefinition } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { type MouseEvent, useState } from 'react';

export interface DocumentViewerControlButtonProps {
  label: string;
  icon: IconDefinition;
  testId: string;
  onClick: (event: MouseEvent<HTMLButtonElement>) => void;
  telemetryInteractionName: TelemetryUserInteraction;
}

export const DocumentViewerControlButton = ({
  label,
  icon,
  onClick,
  testId,
  telemetryInteractionName,
}: DocumentViewerControlButtonProps) => {
  const [isProcessing, setIsProcessing] = useState(false);

  return (
    <Button
      ariaLabel={label}
      testId={testId}
      type="button"
      size="sm"
      variant="circular"
      telemetryInteractionName={telemetryInteractionName}
      className={tw`flex h-fit w-fit items-center justify-center rounded-full bg-content-main p-2 hover:bg-content-subtle/25 active:bg-content-subtle/25`}
      onClick={(event) => {
        // Start a short timeout after which we will trigger the click action
        setTimeout(() => {
          onClick(event);
          setTimeout(() => {
            // After the operation is complete, set the processing state back to false
            setIsProcessing(false);
          }, 400);
        }, 50);

        // Request an animation frame and set processing state to true; this ensures the component displays the loading
        // state before starting processing which may block the main thread
        requestAnimationFrame(() => {
          setIsProcessing(true);
        });
      }}
    >
      {isProcessing ? (
        <LoadingSpinner />
      ) : (
        <FontAwesomeIcon className={tw`h-5 w-5 text-white`} icon={icon} />
      )}
    </Button>
  );
};
